/**
 * Header clock-source pill — always-visible readout of which clock OPButtons
 * is reading from the primary OPBridge (`GET /clock/status` `source`).
 *
 * Supersedes the 2026-07-14 "Connect-card-only" decision (clockPoll.ts): that
 * call reasoned the BPM "live" cue already signalled a live clock, so the
 * source label lived only inside the Connect popup. The operator asked for an
 * always-on header surface (2026-07-18) so the active clock source is legible
 * at a glance during a gig without opening Connect. The Connect card stays as
 * the detailed view; this pill is the ambient one. Both render the same
 * `clockSourceLabel(clockSource())` so they never disagree.
 *
 * States (LED + msg tone):
 *   - ok   (green) : a fresh live clock is anchoring the transport (`isLive`),
 *                    e.g. Ableton is playing → "Ableton (Sync)".
 *   - warn (amber) : OPBridge reports a source but it isn't currently live
 *                    (Ableton connected but stopped/stale) → last-known label.
 *   - idle (dim)   : no source read at all (no primary, or Ableton never seen)
 *                    → "No clock". Deliberately grey, not the alarm-red the
 *                    OPBridge badge uses for `down` — a stopped rig isn't an error.
 *
 * Non-interactive: pure status. The adjacent OPBridge badge owns the click →
 * Connect affordance; duplicating it here would just be a second hit target
 * for the same popup.
 */

import { type Accessor } from 'solid-js';
import { clockSource, clockSourceLabel } from '../opbridge/clockPoll.ts';

interface ClockSourcePillProps {
  /** True when a fresh live clock is anchoring the transport (the same
   *  `isSlaved` the header uses for the 2-decimal BPM + "Live" cue). Drives
   *  the green/amber/idle state. */
  isLive: Accessor<boolean>;
}

export function ClockSourcePill(props: ClockSourcePillProps) {
  const label = () => clockSourceLabel(clockSource());
  const state = (): 'ok' | 'warn' | 'idle' => {
    if (props.isLive()) return 'ok';
    return clockSource() ? 'warn' : 'idle';
  };
  return (
    <div
      class="bridge-status clock-pill"
      data-state={state()}
      title={`Clock source - ${label()}${props.isLive() ? ' · live' : ''}`}
      aria-label={`Clock source: ${label()}`}
    >
      <span class="led" aria-hidden="true" />
      <span class="msg clock-pill-msg">{label()}</span>
    </div>
  );
}
